import { useTranslation } from "react-i18next";

import { Checkbox, Label } from "~/components/ui";
import { cn } from "~/lib/utils";

// ─── Types ──────────────────────────────────────────────────────────────────

interface DipRunRailScopeJob {
  id: string;
  name: string;
}

type Scope = "all" | "step1" | "step2" | "custom";

interface DipRunRailScopeProps {
  /** When true: scope buttons and job checkboxes are non-interactive. */
  readOnly?: boolean;
  /** Active scope. */
  scope: Scope;
  /** Set of selected job ids. */
  selected: Set<string>;
  beJobs: DipRunRailScopeJob[];
  bcrJobs: DipRunRailScopeJob[];
  /** Called when the user clicks a scope button. */
  applyScope: (scope: Scope) => void;
  /** Called when the user toggles a job checkbox. */
  toggleJob: (id: string) => void;
  /**
   * Optional extra content rendered at the bottom of the BE jobs section
   * (e.g. the auto-commit switch). Not rendered in readOnly mode.
   */
  beStepOption?: React.ReactNode;
  /**
   * Optional extra content rendered at the bottom of the BCR jobs section
   * (e.g. the auto-approve switch). Not rendered in readOnly mode.
   */
  bcrStepOption?: React.ReactNode;
}

// ─── Sub-component: single job checkbox row ──────────────────────────────────

interface JobCheckboxRowProps {
  job: DipRunRailScopeJob;
  checked: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
}

function JobCheckboxRow({ job, checked, disabled, onToggle }: JobCheckboxRowProps) {
  const checkboxId = `run-rail-job-${job.id}`;
  return (
    <div className="flex items-center gap-2 py-1">
      <Checkbox
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => !disabled && onToggle(job.id)}
      />
      <Label
        htmlFor={checkboxId}
        className={cn("text-[13px] text-fg-1", disabled ? "cursor-default" : "cursor-pointer")}
      >
        {job.name}
      </Label>
    </div>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SCOPE_VALUES: Scope[] = ["all", "step1", "step2", "custom"];

// ─── Sub-component: section eyebrow label ────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-[12px] font-medium text-fg-3 uppercase tracking-wide">{children}</span>;
}

// ─── Main export: scope segmented control + BE/BCR job lists ─────────────────

export function DipRunRailScope({
  readOnly = false,
  scope,
  selected,
  beJobs,
  bcrJobs,
  applyScope,
  toggleJob,
  beStepOption,
  bcrStepOption,
}: DipRunRailScopeProps) {
  const { t } = useTranslation();

  const scopeLabels: Record<Scope, string> = {
    all: t("feature.dataImportProjects.v2.run.scope.all"),
    step1: t("feature.dataImportProjects.v2.run.scope.step1"),
    step2: t("feature.dataImportProjects.v2.run.scope.step2"),
    custom: t("feature.dataImportProjects.v2.run.scope.custom"),
  };

  return (
    <>
      {/* Scope segmented control */}
      <div className="flex flex-col gap-1.5">
        <SectionLabel>{t("feature.dataImportProjects.v2.run.scope.label")}</SectionLabel>
        <div className="flex gap-1">
          {SCOPE_VALUES.map((value) => (
            <button
              key={value}
              type="button"
              disabled={readOnly}
              onClick={() => !readOnly && applyScope(value)}
              className={cn(
                "flex-1 rounded-[var(--radius-sm)] border px-2 py-1 text-[12px] font-medium transition-colors duration-150",
                scope === value
                  ? "border-primary-500 bg-primary-500 text-white"
                  : "border-border-default bg-white text-fg-2 hover:bg-primary-50 hover:text-primary-700",
                readOnly && "disabled:pointer-events-none disabled:opacity-60",
              )}
            >
              {scopeLabels[value]}
            </button>
          ))}
        </div>
      </div>

      {/* BE jobs section */}
      <div className="flex flex-col gap-1">
        <SectionLabel>{t("model.batchEvidencesJob.modelName")}</SectionLabel>
        {beJobs.map((job) => (
          <JobCheckboxRow
            key={job.id}
            job={job}
            checked={selected.has(job.id)}
            disabled={readOnly}
            onToggle={toggleJob}
          />
        ))}
        {/* Per-step slot (e.g. auto-commit switch in run mode) */}
        {!readOnly && beStepOption}
      </div>

      {/* BCR jobs section */}
      <div className="flex flex-col gap-1">
        <SectionLabel>{t("model.batchChangeRequestsJob.modelName")}</SectionLabel>
        {bcrJobs.map((job) => (
          <JobCheckboxRow
            key={job.id}
            job={job}
            checked={selected.has(job.id)}
            disabled={readOnly}
            onToggle={toggleJob}
          />
        ))}
        {/* Per-step slot (e.g. auto-approve switch in run mode) */}
        {!readOnly && bcrStepOption}
      </div>
    </>
  );
}
